import { getAllCertificates, addCertificate, deleteCertificate } from './db.js';
import { getSeaTimeData, saveSeaTimeEntry, deleteSeaTimeEntry, getContractData, saveContractEntry, deleteContractEntry, ensureSeaTimeId, ensureContractId, getExpenseData, saveExpenseEntry, deleteExpenseEntry, ensureExpenseId } from './storage.js';
import { showNotification } from './utils.js';
import { getNotifPreference, setNotifPreference, requestNotificationPermission } from './notifications.js';
import { renderDashboard, renderCertificates } from './certificates.js';
import { switchTab } from './main.js';

async function exportCertificates() {
    try {
        const certificates = await getAllCertificates();
        const seaTimeData = await getSeaTimeData(); // now async
      const contractData = await getContractData(); // must have "await" now
      const expenseData = await getExpenseData(); // now needs await

        if (certificates.length === 0 && seaTimeData.length === 0 && contractData.length === 0 && expenseData.length === 0) {
            showNotification('No data to export');
            return;
        }

        const exportData = {
            version: '1.3.0',
            exportDate: new Date().toISOString(),
            certificateCount: certificates.length,
            certificates,
            seaTimeLedger: seaTimeData,
            contracts: contractData,
            expenses: expenseData
        };

        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const date = new Date().toISOString().split('T')[0];
        const a = document.createElement('a');
        a.href = url;
        a.download = `seafarer-full-backup-${date}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showNotification('✓ Exported Full Backup');
    } catch (error) {
        console.error('Export error:', error);
        showNotification('✗ Export failed: ' + error.message);
    }
}

// Note: Sea-Time/Contracts UI (tools.js) is lazy-loaded, so import
// here only touches localStorage directly via storage.js. If the
// user is currently on the Sea-Time/Contracts tab when they import,
// they'll see fresh data next time they switch tabs (switchTab always
// re-renders from storage on entry).
async function importCertificates(file, mode) {
    try {
        const text = await file.text();
        const data = JSON.parse(text);

        if (!data.certificates) throw new Error('Invalid backup file format');

        const importedCerts = data.certificates;
        let importedCount = 0;

        if (mode === 'replace') {
            const existing = await getAllCertificates();
            for (const cert of existing) await deleteCertificate(cert.id);
        }

        for (const cert of importedCerts) {
            const { id, ...certData } = cert;
            if (mode === 'merge') {
                const existing = await getAllCertificates();
                const duplicate = existing.find(c =>
                    (cert.certNumber && c.certNumber === cert.certNumber) ||
                    (c.name === cert.name && c.expiryDate === cert.expiryDate)
                );
                if (duplicate) continue;
            }
            await addCertificate(certData);
            importedCount++;
        }

        // ---- Sea Time (IndexedDB, one entry at a time) ----
        if (data.seaTimeLedger) {
            const incoming = data.seaTimeLedger.map(ensureSeaTimeId);
            if (mode === 'replace') {
                const existing = await getSeaTimeData();
                for (const item of existing) await deleteSeaTimeEntry(item.id);
                for (const entry of incoming) await saveSeaTimeEntry(entry);
            } else {
                const existing = await getSeaTimeData();
                const existingIds = new Set(existing.map(i => i.id));
                const toAdd = incoming.filter(i => !existingIds.has(i.id));
                for (const entry of toAdd) await saveSeaTimeEntry(entry);
            }
        }
        //contract section now using indexDB

        if (data.contracts) {
            const incoming = data.contracts.map(ensureContractId);
            if (mode === 'replace') {
                const existing = await getContractData();
                for (const item of existing) await deleteContractEntry(item.id);
                for (const entry of incoming) await saveContractEntry(entry);
            } else {
                const existing = await getContractData();
                const existingIds = new Set(existing.map(i => i.id));
                const toAdd = incoming.filter(i => !existingIds.has(i.id));
                for (const entry of toAdd) await saveContractEntry(entry);
            }
        }

    //expenses section now using indexDB

        if (data.expenses) {
            const incoming = data.expenses.map(ensureExpenseId);
            if (mode === 'replace') {
                const existing = await getExpenseData();
                for (const item of existing) await deleteExpenseEntry(item.id);
                for (const entry of incoming) await saveExpenseEntry(entry);
            } else {
                const existing = await getExpenseData();
                const existingIds = new Set(existing.map(i => i.id));
                const toAdd = incoming.filter(i => !existingIds.has(i.id));
                for (const entry of toAdd) await saveExpenseEntry(entry);
            }
        }

        showNotification(`✓ Imported ${importedCount} certificates & tools data`);
        await renderDashboard();
        await renderCertificates();

        return importedCount;
    } catch (error) {
        console.error('Import error:', error);
        showNotification('✗ Import failed: ' + error.message);
        throw error;
    }
}

async function clearAllCertificates() {
    if (!confirm('⚠️ WARNING: This will permanently delete ALL certificates from this device.\n\nAre you sure? This cannot be undone!')) return;
    if (!confirm('Final confirmation: Delete ALL data?')) return;

    try {
        const certificates = await getAllCertificates();
        for (const cert of certificates) await deleteCertificate(cert.id);

        showNotification('✓ All certificates deleted');
        await renderDashboard();
        await renderCertificates();

        document.getElementById('settings-modal').classList.remove('active');
    } catch (error) {
        console.error('Clear error:', error);
        showNotification('✗ Clear failed: ' + error.message);
    }
}

function showImportPreview(file) {
    const reader = new FileReader();
    reader.onload = async (e) => {
        try {
            const data = JSON.parse(e.target.result);
            if (!data.certificates || !Array.isArray(data.certificates)) throw new Error('Invalid file format');

            document.getElementById('import-count').textContent = data.certificates.length;
            document.getElementById('import-preview').style.display = 'block';
            window.pendingImportFile = file;
        } catch (error) {
            showNotification('✗ Invalid backup file: ' + error.message);
        }
    };
    reader.readAsText(file);
}

function updateNotifToggleUI(notifToggle, notifStatusText) {
    if (!notifToggle) return;

    if (!('Notification' in window)) {
        notifToggle.disabled = true;
        if (notifStatusText) notifStatusText.textContent = 'Notifications are not supported on this device.';
        return;
    }

    if (Notification.permission === 'denied') {
        notifToggle.checked = false;
        notifToggle.disabled = true;
        if (notifStatusText) notifStatusText.textContent =
            'Notifications are blocked at the browser level. Enable them in your browser/app settings first.';
        return;
    }

    notifToggle.disabled = false;
    const prefOn = getNotifPreference() && Notification.permission === 'granted';
    notifToggle.checked = prefOn;
    if (notifStatusText) {
        notifStatusText.textContent = prefOn
            ? '✓ You\'ll be notified when certificates are expiring.'
            : 'Get notified when certificates are expiring soon.';
    }
}

export function initSettings() {
    const settingsBtn = document.getElementById('settings-btn');
    const settingsModal = document.getElementById('settings-modal');
    const closeSettings = document.getElementById('close-settings');
    const exportBtn = document.getElementById('export-btn');
    const importBtn = document.getElementById('import-btn');
    const importFile = document.getElementById('import-file');
    const confirmImportBtn = document.getElementById('confirm-import-btn');
    const cancelImportBtn = document.getElementById('cancel-import-btn');
    const clearAllBtn = document.getElementById('clear-all-btn');
    const notifStatusText = document.getElementById('notif-status-text');
    const notifToggle = document.getElementById('notif-toggle');
    const backToTopBtn = document.getElementById('back-to-top-btn');
    const printPdfBtn = document.getElementById('print-pdf-btn');

    if (notifToggle) {
        notifToggle.addEventListener('change', async () => {
            if (notifToggle.checked) {
                if (Notification.permission !== 'granted') await requestNotificationPermission();
                if (Notification.permission === 'granted') {
                    setNotifPreference(true);
                } else {
                    notifToggle.checked = false;
                    setNotifPreference(false);
                }
            } else {
                setNotifPreference(false);
            }
            updateNotifToggleUI(notifToggle, notifStatusText);
        });
    }
    updateNotifToggleUI(notifToggle, notifStatusText);

    if (settingsBtn) {
        settingsBtn.addEventListener('click', () => {
            settingsModal.classList.add('active');
            updateNotifToggleUI(notifToggle, notifStatusText);
        });
    }

    if (closeSettings) {
        closeSettings.addEventListener('click', () => {
            settingsModal.classList.remove('active');
            document.getElementById('import-preview').style.display = 'none';
        });
    }

    if (settingsModal) {
        settingsModal.addEventListener('click', (e) => {
            if (e.target === settingsModal) {
                settingsModal.classList.remove('active');
                document.getElementById('import-preview').style.display = 'none';
            }
        });
    }

    if (exportBtn) exportBtn.addEventListener('click', exportCertificates);
    if (importBtn) importBtn.addEventListener('click', () => importFile.click());

    if (importFile) {
        importFile.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) showImportPreview(file);
        });
    }

    if (confirmImportBtn) {
        confirmImportBtn.addEventListener('click', async () => {
            const mode = document.querySelector('input[name="import-mode"]:checked').value;
            const file = window.pendingImportFile;
            if (!file) { showNotification('No file selected'); return; }
            try {
                await importCertificates(file, mode);
                document.getElementById('import-preview').style.display = 'none';
                importFile.value = '';
                window.pendingImportFile = null;
            } catch (error) { /* already shown */ }
        });
    }

    if (cancelImportBtn) {
        cancelImportBtn.addEventListener('click', () => {
            document.getElementById('import-preview').style.display = 'none';
            importFile.value = '';
            window.pendingImportFile = null;
        });
    }

    if (clearAllBtn) clearAllBtn.addEventListener('click', clearAllCertificates);

    if (backToTopBtn) {
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            if (scrollTimeout) return;
            scrollTimeout = setTimeout(() => {
                if (window.scrollY > 300) {
                    backToTopBtn.hidden = false;
                    backToTopBtn.classList.add('visible');
                } else {
                    backToTopBtn.classList.remove('visible');
                }
                scrollTimeout = null;
            }, 100);
        });
        backToTopBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    }

    if (printPdfBtn) {
        printPdfBtn.addEventListener('click', async () => {
            settingsModal.classList.remove('active');
            switchTab('certificates');
            document.getElementById('search-input').value = '';
            await renderCertificates();
            setTimeout(() => window.print(), 300);
        });
    }
}
